import { SVT_CORNER } from '@eliadmin/core/src/components/ui/svetech-shape';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Slot } from '@radix-ui/react-slot';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as React from 'react';

/*
 * SveTech card (Key design „Karta“): white with a 1 px #dfe2db border, radius 24 → 32 px with the
 * square top-right corner (from a variable, so a consumer's `rounded-*` still wins); `secondary` is
 * the sunken #f2f3ef card without a border.
 */
const cardVariants = cva(
  ['flex flex-col [--svt-radius:24px] md:[--svt-radius:32px]', SVT_CORNER, 'ea-card'],
  {
    variants: {
      variant: {
        default: ['border border-[#dfe2db] bg-white text-[#14171a]', 'ea-card--default'],
        outlined: ['border border-[#dfe2db] bg-white text-[#14171a]', 'ea-card--outlined'],
        primary: ['bg-[#2c7a25] text-white', 'ea-card--primary'],
        secondary: ['bg-[#f2f3ef] text-[#14171a]', 'ea-card--secondary'],
      },
      elevation: {
        none: ['shadow-none', 'ea-card--elevation-none'],
        xs: ['shadow-xs', 'ea-card--elevation-xs'],
        sm: ['shadow-sm', 'ea-card--elevation-sm'],
        md: ['shadow-md', 'ea-card--elevation-md'],
        lg: ['shadow-lg', 'ea-card--elevation-lg'],
        xl: ['shadow-xl', 'ea-card--elevation-xl'],
        '2xl': ['shadow-2xl', 'ea-card--elevation-2xl'],
      },
    },
    defaultVariants: {
      variant: 'default',
      elevation: 'none',
    },
  },
);

const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> &
    VariantProps<typeof cardVariants> & { asChild?: boolean; tag?: string }
>(({ className, variant, asChild, tag = 'div', ...props }, ref) => {
  const Comp = asChild ? Slot : tag;

  return (
    <Comp
      ref={ref}
      className={cn(cardVariants({ variant: variant, className }), className)}
      {...props}
    />
  );
});
Card.displayName = 'Card';

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex flex-col space-y-1.5 p-6', 'ea-card__header', className)}
      {...props}
    />
  ),
);
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn('svt-title-card', 'ea-card__title', className)} {...props} />
  ),
);
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('svt-body text-[#4a5560]', 'ea-card__description', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-6 pt-0', 'ea-card__content', className)} {...props} />
  ),
);
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-center p-6 pt-0', 'ea-card__footer', className)}
      {...props}
    />
  ),
);
CardFooter.displayName = 'CardFooter';

export { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle };
